import { API_ROUTES } from "@/config/apiRoutes";
import { AUTH_STORAGE_KEYS } from "@/Utils/Auth/authConstants";

export interface VolunteerSessionUser {
  volunteer_id: number;
  organization_id: number;
  display_name: string;
  email?: string | null;
}

const VOLUNTEER_STORAGE_KEY = AUTH_STORAGE_KEYS.VOLUNTEER_USER;

/**
 * Reads the persisted (non-sensitive) volunteer for optimistic hydration on
 * reload. The auth tokens live in httpOnly cookies, never in JS-readable
 * storage. Returns null when nothing valid is stored.
 */
export function readStoredVolunteer(): VolunteerSessionUser | null {
  if (typeof window === "undefined") {
    return null;
  }

  const raw = window.localStorage.getItem(VOLUNTEER_STORAGE_KEY);

  if (!raw) {
    return null;
  }

  try {
    const user = JSON.parse(raw) as VolunteerSessionUser;

    if (
      typeof user.volunteer_id !== "number" ||
      typeof user.display_name !== "string"
    ) {
      return null;
    }

    return user;
  } catch {
    clearStoredVolunteer();
    return null;
  }
}

/** Persists the non-sensitive volunteer object (never tokens). */
export function writeStoredVolunteer(user: VolunteerSessionUser): void {
  if (typeof window === "undefined") {
    return;
  }

  window.localStorage.setItem(VOLUNTEER_STORAGE_KEY, JSON.stringify(user));
}

/** Removes the persisted volunteer. */
export function clearStoredVolunteer(): void {
  if (typeof window === "undefined") {
    return;
  }

  window.localStorage.removeItem(VOLUNTEER_STORAGE_KEY);
}

/**
 * Asks the backend to rotate the volunteer token pair using the httpOnly
 * refresh cookie. Returns whether the refresh succeeded.
 */
export async function refreshVolunteerSession(): Promise<boolean> {
  try {
    const response = await fetch(API_ROUTES.volunteerAuth.refresh, {
      method: "POST",
      credentials: "include",
    });

    return response.ok;
  } catch {
    return false;
  }
}

interface VolunteerMePayload {
  volunteer_id: number;
  organization_id: number;
  email: string | null;
}

/** Resolves the current volunteer identity from the access-token cookie. */
async function requestCurrentVolunteer(): Promise<VolunteerMePayload | null> {
  const response = await fetch(API_ROUTES.volunteerAuth.me, {
    credentials: "include",
  });

  if (!response.ok) {
    return null;
  }

  const data = (await response.json()) as { user?: VolunteerMePayload };

  if (!data.user || typeof data.user.volunteer_id !== "number") {
    return null;
  }

  return data.user;
}

/**
 * Resolves the current volunteer from the httpOnly session cookie. The /me
 * endpoint returns the token identity (ids + email); the display name is a
 * non-sensitive UI value sourced from the persisted volunteer. Refreshes once
 * if the access cookie has expired. Used to hydrate the store on boot.
 */
export async function fetchCurrentVolunteer(): Promise<VolunteerSessionUser | null> {
  const resolve = (payload: VolunteerMePayload): VolunteerSessionUser => ({
    volunteer_id: payload.volunteer_id,
    organization_id: payload.organization_id,
    email: payload.email,
    display_name: readStoredVolunteer()?.display_name ?? payload.email ?? "",
  });

  try {
    const me = await requestCurrentVolunteer();

    if (me) {
      return resolve(me);
    }

    const refreshed = await refreshVolunteerSession();

    if (!refreshed) {
      return null;
    }

    const retry = await requestCurrentVolunteer();
    return retry ? resolve(retry) : null;
  } catch {
    return null;
  }
}
